import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useEffect } from "react";
import { ArrowRight, Phone, MessageCircle, ArrowUpRight } from "lucide-react";
import { Reveal, RevealLines } from "../components/site/Reveal";
import { ServiceCard } from "../components/site/ServiceCard";
import { Marquee } from "../components/site/Marquee";
import { QuoteForm } from "../components/site/QuoteForm";
import { BeforeAfter } from "../components/site/BeforeAfter";
import { ResponsiveImage, baseFromSrc } from "../components/site/ResponsiveImage";
import { SERVICES, PROJECTS, MANIFESTO, STATS, CONTACT } from "../data/site";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const mediaY = useTransform(scrollYProgress, [0, 1], [0, 90]);

  return (
    <section ref={ref} className="relative flex min-h-[100svh] items-center overflow-hidden bg-void" data-testid="hero-section">
      <div className="sfm-grid-bg pointer-events-none absolute inset-0 opacity-40" />
      {/* ambient depth */}
      <div className="pointer-events-none absolute -right-40 top-1/4 h-[520px] w-[520px] rounded-full opacity-40 blur-3xl" style={{ background: "radial-gradient(circle, rgba(229,169,60,0.22), transparent 70%)" }} />

      <div className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-12 px-5 pb-16 pt-32 sm:px-8 lg:grid-cols-12 lg:gap-10 lg:pb-20">
        {/* Text column — always on solid dark for strong contrast */}
        <div className="lg:col-span-6">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="mb-6 font-mono text-xs uppercase tracking-[0.28em] text-amber"
            data-testid="hero-eyebrow"
          >
            Custom Shopfronts · Illuminated Signs · Commercial Branding
          </motion.p>

          <h1 className="font-display text-4xl font-black uppercase leading-[1.02] tracking-tight text-white sm:text-5xl lg:text-5xl xl:text-6xl" data-testid="hero-heading">
            <RevealLines lines={["Transform your", "storefront into a"]} />
            <RevealLines lines={["customer magnet"]} lineClass="text-amber text-glow" delayStep={0.12} />
          </h1>

          <Reveal delay={0.6} className="mt-6 max-w-xl text-base text-white/70 sm:text-lg">
            <p>
              Design, production and construction under one roof. We craft bespoke shopfronts, illuminated signage and
              full commercial branding built to stand out on Ireland's high streets.
            </p>
          </Reveal>

          <Reveal delay={0.75} className="mt-9 flex flex-wrap items-center gap-4">
            <Link
              to="/contact"
              data-testid="hero-get-quote-btn"
              className="group flex items-center gap-2 rounded-full bg-amber px-7 py-4 font-display text-sm font-bold uppercase tracking-wide text-void transition-transform hover:scale-[1.03] hover:bg-amber-glow"
            >
              Get a Free Quote
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <a
              href={CONTACT.phoneHref}
              data-testid="hero-call-btn"
              className="flex items-center gap-2 rounded-full border border-white/20 px-6 py-4 font-mono text-xs uppercase tracking-widest text-white transition-colors hover:border-amber hover:text-amber"
            >
              <Phone size={16} /> Call the Workshop
            </a>
            <a
              href={CONTACT.whatsappHref}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-whatsapp-btn"
              className="flex items-center gap-2 rounded-full border border-white/20 px-6 py-4 font-mono text-xs uppercase tracking-widest text-white transition-colors hover:border-amber hover:text-amber"
            >
              <MessageCircle size={16} /> WhatsApp
            </a>
          </Reveal>
        </div>

        {/* Media column — overlapping stacked split of two real projects */}
        <motion.div style={{ y: mediaY }} className="relative lg:col-span-6">
          <motion.figure
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="relative m-0 overflow-hidden rounded-2xl border border-white/10 sfm-amber-glow"
          >
            <div className="aspect-[16/11] sm:aspect-[16/10]">
              <ResponsiveImage
                base="hero-reshape"
                alt="ReShape Clinic and JLO Beauty shopfront signage by Shop Front Makers"
                testId="hero-image-primary"
                width={1600}
                height={1000}
                eager
                sizes="(max-width:1024px) 100vw, 50vw"
                className="h-full w-full object-cover object-center"
              />
            </div>
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-void/45 via-transparent to-transparent" />
            <figcaption className="absolute left-4 top-4 max-w-[85%] truncate rounded-full bg-void/70 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-white/85 backdrop-blur">
              ReShape Clinic · Signage &amp; Graphics
            </figcaption>
          </motion.figure>

          <motion.figure
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="relative z-10 -mt-12 ml-auto w-4/5 overflow-hidden rounded-xl border-2 border-void shadow-2xl sm:w-2/3"
          >
            <div className="aspect-[16/11]">
              <ResponsiveImage
                base="hero-mrfade"
                alt="Mr Fade Barber shopfront with illuminated lettering and projecting sign by Shop Front Makers"
                testId="hero-image-secondary"
                width={1600}
                height={1100}
                sizes="(max-width:1024px) 80vw, 33vw"
                className="h-full w-full object-cover object-center"
              />
            </div>
            <figcaption className="absolute left-3 top-3 max-w-[90%] truncate rounded-full bg-amber/90 px-2.5 py-0.5 font-mono text-[9px] uppercase tracking-widest text-void">
              Mr Fade Barber · Illuminated
            </figcaption>
          </motion.figure>
        </motion.div>
      </div>
    </section>
  );
};

const Stats = () => (
  <section className="border-y border-white/10 bg-obsidian" data-testid="stats-section">
    <div className="mx-auto grid max-w-7xl grid-cols-2 divide-x divide-white/10 px-5 sm:px-8 lg:grid-cols-4">
      {STATS.map((s, i) => (
        <Reveal key={s.label} delay={i * 0.08} className="px-4 py-10 text-center sm:py-14">
          <div className="font-display text-4xl font-black text-amber sm:text-5xl">{s.value}</div>
          <div className="mt-2 font-mono text-[11px] uppercase tracking-widest text-white/50">{s.label}</div>
        </Reveal>
      ))}
    </div>
  </section>
);

const Home = () => {
  useEffect(() => {
    document.title = "Shop Front Makers | Shopfronts, Signs & Printing Ireland";
  }, []);
  return (
    <div>
      <Hero />
      <Stats />
      <Marquee />

      {/* Services */}
      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8" data-testid="home-services">
        <Reveal className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-amber">What We Do</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold uppercase tracking-tight text-white sm:text-4xl lg:text-5xl">
              Our Services
            </h2>
          </div>
          <Link to="/services" className="flex items-center gap-1 font-mono text-xs uppercase tracking-widest text-white/60 hover:text-amber">
            View all services <ArrowUpRight size={14} />
          </Link>
        </Reveal>

        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.slice(0, 6).map((s, i) => (
            <ServiceCard key={s.id} service={s} index={i} />
          ))}
        </div>
      </section>

      {/* Manifesto */}
      <section className="border-y border-white/10 bg-obsidian py-24" data-testid="home-manifesto">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-amber">Why Choose Us</p>
            <h2 className="mt-3 max-w-3xl font-display text-3xl font-extrabold uppercase leading-tight tracking-tight text-white sm:text-4xl lg:text-5xl">
              Design, production and construction — as one service
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/5 sm:grid-cols-2">
            {MANIFESTO.map((m, i) => (
              <Reveal key={m.chapter} delay={(i % 2) * 0.08} className="bg-obsidian p-8 sm:p-10">
                <span className="font-mono text-sm text-amber">{m.chapter}</span>
                <h3 className="mt-4 font-display text-2xl font-bold tracking-tight text-white">{m.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-white/60">{m.description}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <BeforeAfter />

      {/* Projects preview */}
      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8" data-testid="home-projects">
        <Reveal className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-amber">Selected Work</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold uppercase tracking-tight text-white sm:text-4xl lg:text-5xl">
              Recent Projects
            </h2>
          </div>
          <Link to="/projects" className="flex items-center gap-1 font-mono text-xs uppercase tracking-widest text-white/60 hover:text-amber">
            View gallery <ArrowUpRight size={14} />
          </Link>
        </Reveal>
        <div className="mt-12 grid gap-6 md:grid-cols-2">
          {PROJECTS.slice(0, 2).map((p, i) => (
            <Reveal key={p.id} delay={i * 0.1}>
              <div className="group relative overflow-hidden rounded-2xl border border-white/10">
                <div className="aspect-[16/10] overflow-hidden">
                  <ResponsiveImage
                    base={baseFromSrc(p.image)}
                    alt={p.title}
                    width={1600}
                    height={1000}
                    sizes="(max-width:768px) 100vw, 50vw"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-void via-void/40 to-transparent" />
                <div className="absolute bottom-0 p-6">
                  <p className="font-mono text-[11px] uppercase tracking-widest text-amber">{p.industry}</p>
                  <h3 className="mt-1 font-display text-2xl font-bold text-white">{p.title}</h3>
                  <p className="mt-1 text-sm text-white/60">{p.service}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Quote CTA */}
      <section className="border-t border-white/10 bg-obsidian py-24" data-testid="home-quote">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-2">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-amber">Start Your Project</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold uppercase leading-tight tracking-tight text-white sm:text-4xl lg:text-5xl">
              Get a free, no-obligation quote
            </h2>
            <p className="mt-5 max-w-md text-white/60">
              Tell us about your shopfront, signage or branding project and we'll come back with ideas and an estimate.
              {" "}{CONTACT.responseNote}
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href={CONTACT.phoneHref} className="flex items-center gap-2 rounded-full border border-white/15 px-5 py-3 font-mono text-xs uppercase tracking-widest text-white hover:border-amber hover:text-amber">
                <Phone size={16} /> {CONTACT.phone}
              </a>
              <a href={CONTACT.whatsappHref} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-full border border-white/15 px-5 py-3 font-mono text-xs uppercase tracking-widest text-white hover:border-amber hover:text-amber">
                <MessageCircle size={16} /> WhatsApp
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <QuoteForm />
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
